package scenes

import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import audio.LocalSoundEngine
import domain.MODE_CONFIGS
import domain.PlayGameMode
import domain.player.GameAction
import domain.player.GraphicsConfigStore
import domain.player.KeyBindingStore
import engine.model.GameSnapshot
import engine.model.MatchConfig
import engine.model.MoveSource
import engine.model.SpinType
import engine.model.events.DefaultGameEvents
import engine.model.events.EventOrchestrator
import engine.model.events.GameEvent
import game.GameMode
import game.GravitarGame
import rendering.AnimatedBackground
import rendering.BLOCK_SIZE
import rendering.BOARD_COLS
import rendering.BOARD_ROWS
import rendering.BoardCanvas
import rendering.DebrisState
import rendering.GameLoop
import rendering.GameOverlay
import rendering.HoldBox
import rendering.NextBox
import rendering.ScoreFeedDisplay
import rendering.ScoreFeedEntry
import rendering.LockFlashState
import rendering.ShakeState
import rendering.TiltState
import rendering.WiggleState
import rendering.ComboDisplay
import rendering.StatLabel
import rendering.StatValue
import rendering.TopBar
import rendering.touchGameInput

/**
 * [GameScene] with full HUD layout (Phase 4).
 *
 * Layout:
 * ```
 * Box(fillMaxSize, black bg, focusable) {
 *     AnimatedBackground()
 *
 *     Column(fillMaxSize) {
 *         TopBar(modeName, b2bCount, elapsedMs)
 *
 *         Row(fillMaxSize, SpaceEvenly) {
 *             // Left panel
 *             Column { HoldBox; stats; ScoreFeedDisplay }
 *
 *             // Board
 *             Box { BoardCanvas }
 *
 *             // Right panel
 *             Column { NextBox x5; objective label + progress }
 *         }
 *     }
 *
 *     GameOverlay(visible = showOverlay, ...)
 * }
 * ```
 *
 * Captures keyboard focus via [FocusRequester] and routes key events to
 * [GravitarGame.onKeyDown] / [GravitarGame.onKeyUp].
 *
 * Subscribes to [DefaultGameEvents] via [EventOrchestrator] to:
 * - Trigger [LocalSoundEngine] playback
 * - Update HUD state (b2bCount, scoreFeedEntries, comboCount)
 * - Trigger debris particle effects on line clear
 *
 * @param navigate     Callback to navigate to another scene.
 * @param mode         The [PlayGameMode] to use for this game session.
 *                     Defaults to [PlayGameMode.SPRINT] when null.
 * @param matchConfig  Optional fully-configured [MatchConfig] for custom sessions.
 */
@Composable
fun GameScene(
    navigate: Navigate = { _, _ -> },
    mode: PlayGameMode? = null,
    matchConfig: MatchConfig? = null,
) {
    // Resolve the effective mode (default to SPRINT)
    val effectiveMode = mode ?: PlayGameMode.SPRINT

    // Resolve the GameMode from the PlayGameMode parameter
    val gameMode = when (effectiveMode) {
        PlayGameMode.TIMEATTACK -> GameMode.TIME_ATTACK
        PlayGameMode.CUSTOM     -> GameMode.CUSTOM
        PlayGameMode.STORY      -> GameMode.CUSTOM
        else                    -> GameMode.SPRINT
    }

    // Resolve mode config for HUD labels
    val modeConfig = MODE_CONFIGS[effectiveMode]

    // Create a game instance for the resolved mode, remembered for the lifetime of this composable
    val game = remember(mode, matchConfig) {
        GravitarGame(gameId = "game-${mode?.name ?: "sprint"}") {
            this.mode = gameMode
            if (matchConfig != null) this.matchConfig = matchConfig
        }
    }

    var snapshot by remember { mutableStateOf<GameSnapshot?>(null) }

    // ── Debris particle state ─────────────────────────────────────────────────
    val debrisState = remember { DebrisState() }

    // ── Shake animation state ─────────────────────────────────────────────────
    val shakeState = remember { ShakeState() }

    // ── Tilt animation state ──────────────────────────────────────────────────
    val tiltState = remember { TiltState() }

    // ── Wiggle animation state ────────────────────────────────────────────────
    val wiggleState = remember { WiggleState() }

    // ── Lock flash animation state ────────────────────────────────────────────
    val lockFlashState = remember { LockFlashState() }
    val coroutineScope = rememberCoroutineScope()
    val graphicsConfig = remember { GraphicsConfigStore.load() }

    // ── HUD state ─────────────────────────────────────────────────────────────
    var b2bCount by remember { mutableStateOf(0) }
    var comboCount by remember { mutableStateOf(0) }
    val scoreFeedEntries = remember { mutableStateListOf<ScoreFeedEntry>() }
    var nextFeedId by remember { mutableStateOf(0L) }

    // ── Overlay state ─────────────────────────────────────────────────────────
    // Derived from game state — show overlay when game is over or goal is met
    val showOverlay = game.isGameOver || game.isGoalMet

    // ── Key tracking ──────────────────────────────────────────────────────────
    // Track currently held keys to detect fresh presses vs. auto-repeat.
    val heldKeys = remember { mutableSetOf<Long>() }

    // Wall-wiggle detection state
    var leftHeld by remember { mutableStateOf(false) }
    var rightHeld by remember { mutableStateOf(false) }

    val focusRequester = remember { FocusRequester() }

    // Resolve which key codes map to MOVE_LEFT / MOVE_RIGHT from the active bindings
    val activeBindings = remember { KeyBindingStore.loadActive() }
    val moveLeftKeys = remember { activeBindings[GameAction.MOVE_LEFT] ?: emptyList() }
    val moveRightKeys = remember { activeBindings[GameAction.MOVE_RIGHT] ?: emptyList() }

    // ── Game loop ─────────────────────────────────────────────────────────────
    // Drive the game loop — updates game state and captures the latest snapshot
    GameLoop(game = game) { deltaMs ->
        game.update(deltaMs)
        snapshot = game.getSnapshot()
        // Update debris particles each frame
        debrisState.update(deltaMs.toFloat())
    }

    // Request focus once so key events are delivered immediately
    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    // Re-request focus when overlay is dismissed (after retry)
    LaunchedEffect(showOverlay) {
        if (!showOverlay) {
            kotlinx.coroutines.delay(100)  // wait for overlay fade-out before reclaiming focus
            focusRequester.requestFocus()
        }
    }

    // ── Sound engine + HUD event subscriptions ────────────────────────────────
    val soundEngine = LocalSoundEngine.current
    val gameId = game.gameId

    DisposableEffect(game) {
        val listeners = mutableListOf<(GameEvent) -> Unit>()

        fun addListener(listener: (GameEvent) -> Unit) {
            EventOrchestrator.listeners.add(listener)
            listeners.add(listener)
        }

        // Sound: piece moved
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceMoved && ev.gameId == gameId && ev.moveSource == MoveSource.PLAYER)
                soundEngine.playMove()
        }
        // Wall wiggle: piece didn't move horizontally despite player input
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceMoved && ev.gameId == gameId
                && ev.moveSource == MoveSource.PLAYER && ev.deltaCol == 0) {
                val dir = when {
                    leftHeld  -> -1
                    rightHeld ->  1
                    else      ->  0
                }
                if (dir != 0) wiggleState.triggerWiggle(coroutineScope, dir)
            }
        }
        // Sound: piece rotated
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceRotated && ev.gameId == gameId)
                soundEngine.playRotate()
        }
        // Tilt: apply spin tilt when a spin is detected during rotation
        addListener { ev ->
            if (ev is DefaultGameEvents.SpinDetected && ev.gameId == gameId && ev.spinType != SpinType.NONE) {
                tiltState.applySpinTilt(coroutineScope, direction = 1, maxDeg = 3.5f)
            }
        }
        // Sound: soft drop
        addListener { ev ->
            if (ev is DefaultGameEvents.SoftDrop && ev.gameId == gameId)
                soundEngine.playSoftDrop()
        }
        // Sound: hard drop (no feed entry)
        addListener { ev ->
            if (ev is DefaultGameEvents.HardDrop && ev.gameId == gameId) {
                soundEngine.playHardDrop()
                shakeState.triggerShake(coroutineScope, intensity = graphicsConfig.boardShake.toFloat())
            }
        }
        // Sound: piece held
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceHeld && ev.gameId == gameId)
                soundEngine.playHold()
        }
        // Sound: piece locked (no lines)
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceLocked && ev.gameId == gameId && !ev.linesCleared)
                soundEngine.playLock()
        }
        // Lock flash: trigger on every piece lock
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceLocked && ev.gameId == gameId)
                lockFlashState.trigger(coroutineScope)
        }
        // Sound + debris + score feed: line cleared
        addListener { ev ->
            if (ev is DefaultGameEvents.LineCleared && ev.gameId == gameId && ev.linesCleared.isNotEmpty()) {
                val count = ev.linesCleared.size
                soundEngine.playLineClear(count)
                shakeState.triggerShake(coroutineScope, intensity = (graphicsConfig.boardShake * 0.4f).toFloat())

                // Trigger debris particles
                val board = game.board
                debrisState.triggerLineClearDebris(
                    clearedRows = ev.linesCleared,
                    getCell = { r, c -> board[r, c] },
                )

                // Score feed label — use StoryRulebook names
                val label = when (count) {
                    1    -> "BLIP"
                    2    -> "BURST"
                    3    -> "FLARE"
                    4    -> "GRAVITAR"
                    in 5..7 -> "GRAVITAR"
                    in 8..11 -> "VOID"
                    in 12..15 -> "COLLAPSE"
                    else -> "EVENT HORIZON"
                }
                val isSpin = false  // no spin detection in GameScene yet
                val entry = ScoreFeedEntry(
                    id = nextFeedId++,
                    label = label,
                    value = "+$count",
                    isSpin = isSpin,
                )
                scoreFeedEntries.add(0, entry)
            }
        }
        // Sound: level up
        addListener { ev ->
            if (ev is DefaultGameEvents.LevelUp && ev.gameId == gameId)
                soundEngine.playLevelUp()
        }
        // Sound: game over
        addListener { ev ->
            if (ev is DefaultGameEvents.GameOver && ev.gameId == gameId)
                soundEngine.playGameOver()
        }
        // HUD: back-to-back counter + B2B badge in feed
        addListener { ev ->
            if (ev is DefaultGameEvents.BackToBackTrigger && ev.gameId == gameId) {
                b2bCount = ev.backToBackCount
                val entry = ScoreFeedEntry(
                    id = nextFeedId++,
                    label = "B2B",
                    value = ev.backToBackCount.toString(),
                    isB2B = true,
                )
                scoreFeedEntries.add(0, entry)
            }
        }
        // HUD: combo counter
        addListener { ev ->
            if (ev is DefaultGameEvents.ComboTriggered && ev.gameId == gameId)
                comboCount = ev.comboCount
        }

        onDispose {
            EventOrchestrator.listeners.removeAll(listeners)
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (showOverlay) return@onKeyEvent false  // let overlay handle it
                // Escape during gameplay → go to menu
                if (event.type == KeyEventType.KeyDown && event.key == androidx.compose.ui.input.key.Key.Escape) {
                    navigate(SceneName.MENU, null)
                    return@onKeyEvent true
                }
                val keyCode = event.key.keyCode
                val keyId = keyCode.toString()
                when (event.type) {
                    KeyEventType.KeyDown -> {
                        val isFresh = heldKeys.add(keyCode)
                        if (keyId in moveLeftKeys) leftHeld = true
                        if (keyId in moveRightKeys) rightHeld = true
                        game.onKeyDown(keyId, isFresh)
                    }
                    KeyEventType.KeyUp -> {
                        heldKeys.remove(keyCode)
                        if (keyId in moveLeftKeys) leftHeld = false
                        if (keyId in moveRightKeys) rightHeld = false
                        game.onKeyUp(keyId)
                    }
                }
                true
            },
    ) {
        // Animated background — behind everything
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        // Main layout column: TopBar + game row
        Column(modifier = Modifier.fillMaxSize()) {
            val snap = snapshot

            // ── Top bar ───────────────────────────────────────────────────────
            val elapsedMs = ((snap?.hudInfo?.sessionTimeSeconds ?: 0.0) * 1000).toLong()
            TopBar(
                modeName = modeConfig?.label ?: effectiveMode.name,
                b2bCount = b2bCount,
                elapsedMs = elapsedMs,
            )

            // ── Game row: left panel | board | right panel ────────────────────
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
            ) {
                val density = LocalDensity.current

                val leftPanelDp  = minOf(100.dp, maxWidth * 0.22f)
                val rightPanelDp = minOf(80.dp,  maxWidth * 0.18f)
                val spacingDp    = 8.dp

                val boardAvailWidthDp = maxWidth - leftPanelDp - rightPanelDp - spacingDp * 4

                val availWidthPx  = with(density) { boardAvailWidthDp.toPx() }
                val availHeightPx = with(density) { maxHeight.toPx() }
                val bsByWidth  = availWidthPx  / BOARD_COLS
                val bsByHeight = availHeightPx / BOARD_ROWS
                val bs = minOf(bsByWidth, bsByHeight, BLOCK_SIZE)

                val boardWidthDp  = with(density) { (BOARD_COLS * bs).toDp() }
                val boardHeightDp = with(density) { (BOARD_ROWS * bs).toDp() }

                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(spacingDp, Alignment.CenterHorizontally),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                // ── Left panel ────────────────────────────────────────────────
                Column(
                    modifier = Modifier
                        .width(leftPanelDp)
                        .wrapContentHeight(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // Hold box
                    HoldBox(piece = snap?.holdPiece)

                    // Combo display
                    ComboDisplay(comboCount = comboCount)

                    // Level stat
                    StatLabel(text = "LEVEL")
                    StatValue(text = snap?.hudInfo?.currentLevel?.toString() ?: "-")

                    // Lines remaining stat
                    if (snap != null && modeConfig != null) {
                        StatLabel(text = modeConfig.goalLabel)
                        StatValue(text = modeConfig.getGoalRemaining(snap))
                    } else {
                        StatLabel(text = "LINES")
                        StatValue(text = "-")
                    }

                    // Score feed
                    ScoreFeedDisplay(
                        entries = scoreFeedEntries,
                        onRemove = { id -> scoreFeedEntries.removeAll { it.id == id } },
                    )
                }

                // ── Board ─────────────────────────────────────────────────────
                Box(contentAlignment = Alignment.Center) {
                    if (snap != null) {
                        BoardCanvas(
                            snapshot = snap,
                            blockSize = bs,
                            modifier = Modifier
                                .size(width = boardWidthDp, height = boardHeightDp)
                                .touchGameInput(game = game, enabled = !showOverlay)
                                .graphicsLayer {
                                    translationX = shakeState.offsetX.value + wiggleState.offsetX.value
                                    translationY = shakeState.offsetY.value
                                    rotationZ = tiltState.rotationZ.value
                                },
                            debrisState = debrisState,
                            lockFlashColor = lockFlashState.color,
                        )
                    }
                }

                // ── Right panel ───────────────────────────────────────────────
                Column(
                    modifier = Modifier
                        .width(rightPanelDp)
                        .wrapContentHeight(),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // 5 next piece preview boxes
                    for (i in 0 until 5) {
                        NextBox(
                            piece = snap?.nextPieces?.getOrNull(i),
                            index = i,
                        )
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    // Objective label + progress
                    if (snap != null && modeConfig != null) {
                        StatLabel(text = modeConfig.goalLabel)
                        StatValue(text = modeConfig.getGoalValue(snap))
                    }
                }
            }
            }
        }

        // ── Game overlay ──────────────────────────────────────────────────────
        GameOverlay(
            visible = showOverlay,
            isGoalMet = game.isGoalMet,
            snap = snapshot,
            modeConfig = game.matchConfig,
            onRetry = {
                game.reset()
                debrisState.clear()
                b2bCount = 0
                comboCount = 0
                scoreFeedEntries.clear()
            },
            onMenu = {
                navigate(SceneName.MENU, null)
            },
        )
    }
}
